import React, { useState, useEffect } from 'react';
import {
  Users,
  CheckCircle,
  Clock,
  AlertTriangle,
  Calendar,
  FileText,
  BarChart3,
  Filter,
  Search,
  Plus,
  UserPlus,
  Target,
  TrendingUp,
  ChevronRight,
  Eye,
  Edit,
  Settings,
  PieChart
} from 'lucide-react';
import { useAuthStore, api } from '../auth/authStore';
import toast from 'react-hot-toast';

interface Task {
  id: string;
  title: string;
  description?: string;
  clientName: string;
  status: 'DRAFT' | 'TODO' | 'IN_PROGRESS' | 'REVIEW' | 'COMPLETED' | 'SUBMITTED';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  dueDate: string;
  assignees: {
    preparerId?: string;
    reviewerId?: string;
    submitterId?: string;
  };
  assigneeNames: {
    preparer?: string;
    reviewer?: string;
    submitter?: string;
  };
  timeSpent: number;
  estimatedTime?: number;
  completionPercentage: number;
  type: string;
  createdAt: string;
}

interface TeamMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  assignedTasks: number;
  completedTasks: number;
  hoursWorked: number;
  utilizationRate: number;
}

interface ManagerStats {
  totalTasks: number;
  teamMembers: number;
  completedThisWeek: number;
  overdueTasksCount: number;
  teamUtilization: number;
  avgCompletionTime: number;
}

export const ManagerTaskDashboard: React.FC = () => {
  const { user } = useAuthStore();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [stats, setStats] = useState<ManagerStats>({
    totalTasks: 0,
    teamMembers: 0,
    completedThisWeek: 0,
    overdueTasksCount: 0,
    teamUtilization: 0,
    avgCompletionTime: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all');
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  useEffect(() => {
    fetchManagerData();
  }, []);

  const fetchManagerData = async () => {
    try {
      setLoading(true);

      // Fetch team tasks
      try {
        const tasksResponse = await api.get('/tasks/manager');
        if (tasksResponse.data.success) {
          setTasks(tasksResponse.data.data || []);
        }
      } catch (tasksError) {
        console.error('Error fetching team tasks:', tasksError);
      }

      // Fetch team members
      try {
        const teamResponse = await api.get('/users/team-members');
        if (teamResponse.data.success) {
          setTeamMembers(teamResponse.data.data || []);
        }
      } catch (teamError) {
        console.error('Error fetching team members:', teamError);
      }

      calculateStats();
    } catch (error) {
      console.error('Error fetching manager data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const thisWeekStart = new Date();
    thisWeekStart.setDate(thisWeekStart.getDate() - thisWeekStart.getDay());

    const completedThisWeek = tasks.filter(task => 
      task.status === 'COMPLETED' && 
      new Date(task.createdAt) >= thisWeekStart
    ).length;

    const overdueTasksCount = tasks.filter(task => 
      new Date(task.dueDate) < new Date() && 
      !['COMPLETED', 'SUBMITTED'].includes(task.status)
    ).length;

    const totalUtilization = teamMembers.reduce((sum, member) => sum + member.utilizationRate, 0);
    const teamUtilization = teamMembers.length > 0 ? totalUtilization / teamMembers.length : 0;

    const completedTasks = tasks.filter(task => task.status === 'COMPLETED');
    const avgCompletionTime = completedTasks.length > 0 
      ? completedTasks.reduce((sum, task) => sum + task.timeSpent, 0) / completedTasks.length / 60
      : 0;

    setStats({
      totalTasks: tasks.length,
      teamMembers: teamMembers.length,
      completedThisWeek,
      overdueTasksCount,
      teamUtilization,
      avgCompletionTime
    });
  };

  const assignTask = async (taskId: string, assigneeId: string, role: 'preparer' | 'reviewer' | 'submitter') => {
    try {
      const response = await api.patch(`/tasks/${taskId}/assign`, { assigneeId, role });
      if (response.data.success) {
        toast.success('Task assigned successfully');
        fetchManagerData();
      }
    } catch (error) {
      console.error('Error assigning task:', error);
      toast.error('Failed to assign task');
    }
  };

  const updateTaskStatus = async (taskId: string, newStatus: string) => {
    try {
      const response = await api.patch(`/tasks/${taskId}/status`, { status: newStatus });
      if (response.data.success) {
        setTasks(tasks.map(task =>
          task.id === taskId ? { ...task, status: newStatus as any } : task
        ));
        toast.success('Task status updated');
        calculateStats();
      }
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error('Failed to update task status');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'TODO': return 'bg-blue-100 text-blue-800';
      case 'IN_PROGRESS': return 'bg-yellow-100 text-yellow-800';
      case 'REVIEW': return 'bg-purple-100 text-purple-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'SUBMITTED': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-600';
      case 'high': return 'text-orange-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         task.clientName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
    const matchesAssignee = assigneeFilter === 'all' || 
                           task.assignees.preparerId === assigneeFilter ||
                           task.assignees.reviewerId === assigneeFilter ||
                           task.assignees.submitterId === assigneeFilter;
    
    return matchesSearch && matchesStatus && matchesAssignee;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Team Task Management</h1>
              <p className="text-gray-600 mt-1">
                Welcome back, {user?.firstName}! Manage your team's tasks and performance.
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button 
                onClick={() => setShowCreateTask(true)}
                className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Task
              </button>
              <button className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                <BarChart3 className="w-4 h-4 mr-2" />
                Analytics
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Tasks</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalTasks}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Completed This Week</p>
                <p className="text-2xl font-bold text-gray-900">{stats.completedThisWeek}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Team Members</p>
                <p className="text-2xl font-bold text-gray-900">{stats.teamMembers}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Overdue Tasks</p>
                <p className="text-2xl font-bold text-gray-900">{stats.overdueTasksCount}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Team Performance */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Team Performance</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {teamMembers.map((member) => (
                  <div key={member.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                        <span className="text-primary-600 font-medium">
                          {member.firstName[0]}{member.lastName[0]}
                        </span>
                      </div>
                      <div className="ml-3">
                        <p className="font-medium text-gray-900">
                          {member.firstName} {member.lastName}
                        </p>
                        <p className="text-sm text-gray-500">{member.role}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-6 text-sm">
                      <div className="text-center">
                        <p className="font-medium text-gray-900">{member.assignedTasks}</p>
                        <p className="text-gray-500">Assigned</p>
                      </div>
                      <div className="text-center">
                        <p className="font-medium text-gray-900">{member.completedTasks}</p>
                        <p className="text-gray-500">Completed</p>
                      </div>
                      <div className="text-center">
                        <p className="font-medium text-gray-900">{member.hoursWorked}h</p>
                        <p className="text-gray-500">Hours</p>
                      </div>
                      <div className="text-center">
                        <p className="font-medium text-gray-900">{member.utilizationRate}%</p>
                        <p className="text-gray-500">Utilization</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Quick Stats</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Team Utilization</span>
                <span className="font-medium">{stats.teamUtilization.toFixed(1)}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Avg Completion Time</span>
                <span className="font-medium">{stats.avgCompletionTime.toFixed(1)}h</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Tasks This Month</span>
                <span className="font-medium">{tasks.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Completion Rate</span>
                <span className="font-medium">
                  {tasks.length > 0 ? ((tasks.filter(t => t.status === 'COMPLETED').length / tasks.length) * 100).toFixed(1) : 0}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search tasks..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="TODO">To Do</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="REVIEW">Review</option>
                <option value="COMPLETED">Completed</option>
              </select>
              <select
                value={assigneeFilter}
                onChange={(e) => setAssigneeFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Assignees</option>
                {teamMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.firstName} {member.lastName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Tasks List */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Team Tasks</h2>
            <div className="space-y-4">
              {filteredTasks.map((task) => (
                <div key={task.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="font-medium text-gray-900">{task.title}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                          {task.status.replace('_', ' ')}
                        </span>
                        <span className={`text-xs font-medium ${getPriorityColor(task.priority)}`}>
                          {task.priority.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{task.clientName}</p>
                      <div className="flex items-center space-x-4 text-xs text-gray-500 mb-2">
                        <span className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          Due: {new Date(task.dueDate).toLocaleDateString()}
                        </span>
                        <span className="flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {Math.floor(task.timeSpent / 60)}h {task.timeSpent % 60}m
                        </span>
                        <span className="flex items-center">
                          <Target className="w-3 h-3 mr-1" />
                          {task.completionPercentage}% complete
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 text-xs">
                        {task.assigneeNames.preparer && (
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            Preparer: {task.assigneeNames.preparer}
                          </span>
                        )}
                        {task.assigneeNames.reviewer && (
                          <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">
                            Reviewer: {task.assigneeNames.reviewer}
                          </span>
                        )}
                        {task.assigneeNames.submitter && (
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                            Submitter: {task.assigneeNames.submitter}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <button className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg">
                        <UserPlus className="w-4 h-4" />
                      </button>
                      <select
                        value={task.status}
                        onChange={(e) => updateTaskStatus(task.id, e.target.value)}
                        className="text-xs border border-gray-300 rounded px-2 py-1"
                      >
                        <option value="DRAFT">Draft</option>
                        <option value="TODO">To Do</option>
                        <option value="IN_PROGRESS">In Progress</option>
                        <option value="REVIEW">Review</option>
                        <option value="COMPLETED">Completed</option>
                        <option value="SUBMITTED">Submitted</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
              {filteredTasks.length === 0 && (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No tasks found matching your criteria.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
